import { GetWSMgr } from "@/Utils/StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { kDayList } from "@coreapp/constants";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QWaitingPanel } from "@coreapp/CoreApp/QWaitingPanel";
import { QPointInTimeRFC } from "@coreapp/CoreApp/QPointInTimeRFC";
import { appstr } from "@coreapp/utils/appstr";
import { uFramelessPanelColor } from "@coreapp/whitelabel/brand";

export const monthlyOptions = ["First Day", "Last Day"];

export const getPointInTime = chart => {
  const pit = { ...chart?.["pointintime"] };

  // Default frequency if not explicit
  // From: QDatabaseConfigPanel.DoPITSettings
  if (isNullish(pit.frequency)) {
    pit.frequency = {
      period: "Weekly",
      day: "Wednesday"
    };
  }

  if (isNullish(pit.pitlist)) {
    pit.pitlist = [];
  }

  switch (pit.frequency.period) {
    case "Weekly":
    case "Bi-Weekly":
      if (pit.frequency.day === undefined || !kDayList.includes(pit.frequency.day)) {
        pit.frequency.day = kDayList[0];
      }
      break;
    case "Monthly":
    case "Quarterly":
      if (pit.frequency.day === undefined || !monthlyOptions.includes(pit.frequency.day)) {
        pit.frequency.day = monthlyOptions[0];
      }
      break;
  }
  return pit;
};

export const dateStringToDateObject = dateString => {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  return new Date(year, month - 1, day);
};

export const getResetDateFilters = chartObj => {
  const pit = getPointInTime(chartObj);
  if (isNullish(pit)) {
    return [null, null];
  }

  const pitList = pit.pitlist.map(a => {
    return a.split(":")[0];
  });

  let nextDateFrom = null;
  let nextDateTo = null;
  if (pitList.length > 0) {
    nextDateFrom = dateStringToDateObject(pitList[0]);
    nextDateTo = dateStringToDateObject(pitList[pitList.length - 1]);
  }

  return [nextDateFrom, nextDateTo];
};

export const getArchiveLabel = archive => {
  const wsMgr = GetWSMgr();
  const oi = wsMgr?.GetOrgInfo();
  const splitResult = archive.split(":");
  const theDate = splitResult[0];
  const theTime = splitResult[1] ?? null;

  const dateObj = convertStdDateString(theDate, theTime);
  const localTime = dateObj.toLocaleString("en-EN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: (oi.GetTimeFormat() ? oi.GetTimeFormat() : 12) === 12
  });
  let dateLabel = dateObj.FormatDate(oi.GetDateFormat());

  if (theTime !== null && localTime !== "null" && localTime !== "Invalid Date")
    dateLabel += ", " + localTime;

  return dateLabel;
};

// "manualpit" command from QDatabaseConfigPanel
export const createManualPIT = chart => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();
    const callbackObj = {
      fWSMgr: wsMgr,
      resolve: data => {
        resolve(data);
      },
      reject
    };

    const theAccountName = wsMgr.GetAccountName();
    const theUsername = wsMgr.GetUserName();
    const manualPITRFC = new QPointInTimeRFC();
    manualPITRFC.InitQPointInTimeRFC(callbackObj);
    const theData = JSON.stringify({});
    manualPITRFC.SetParams(theAccountName, theUsername, "manualPointInTime", theData, chart["ID"]);
    Globals._rfcMgr.QueueRFC(manualPITRFC);
    const waitingPanel = new QWaitingPanel();
    waitingPanel.SetMsgColor(uFramelessPanelColor);
    waitingPanel.InitWaitingPanel(
      appstr("Creating Archive") + "...",
      wsMgr.fBackgroundPanel,
      Globals._wsMgr,
      220
    );
    wsMgr.fWaiting = waitingPanel;
  });
};

export const generateDeltaReport = (chartID, dateRange) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();

    const callbackObj = {
      fWSMgr: wsMgr,
      resolve,
      reject
    };

    const accountName = wsMgr.GetAccountName();
    const username = wsMgr.GetUserName();
    const data = JSON.stringify({
      dateRange
    });
    const deltaRFC = new QPointInTimeRFC();
    deltaRFC.fWSMgr = wsMgr;
    deltaRFC.InitQPointInTimeRFC(callbackObj);
    deltaRFC.SetParams(accountName, username, "doCreateDeltaReport", data, chartID);
    Globals._rfcMgr.QueueRFC(deltaRFC);
    const waitingPanel = new QWaitingPanel();
    waitingPanel.InitWaitingPanel(
      appstr("generatedelta") + "…",
      wsMgr.fBackgroundPanel,
      wsMgr,
      220
    );
    wsMgr.fWaiting = waitingPanel;
  });
};

export const exportPointInTimeArchive = (chartID, hotdate) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();

    const callbackObj = {
      fWSMgr: wsMgr,
      resolve,
      reject
    };

    const accountName = wsMgr.GetAccountName();
    const username = wsMgr.GetUserName();
    const data = JSON.stringify({ hotdate });
    const exportRFC = new QPointInTimeRFC();
    exportRFC.fWSMgr = wsMgr;
    exportRFC.InitQPointInTimeRFC(callbackObj);
    exportRFC.SetParams(accountName, username, "exportArchiveData", data, chartID);
    Globals._rfcMgr.QueueRFC(exportRFC);
    const waitingPanel = new QWaitingPanel();
    waitingPanel.InitWaitingPanel(
      appstr("Exporting archive") + "…",
      wsMgr.fBackgroundPanel,
      wsMgr,
      220
    );
    wsMgr.fWaiting = waitingPanel;
  });
};

export const loadPointInTime = async (chartID, hotdate) => {
  return new Promise((resolve, reject) => {
    const wsMgr = GetWSMgr();

    const callbackObj = {
      fWSMgr: wsMgr,
      resolve,
      reject
    };

    const accountName = wsMgr.GetAccountName();
    const username = wsMgr.GetUserName();
    const data = JSON.stringify({ hotdate });
    const pitRFC = new QPointInTimeRFC();
    pitRFC.InitQPointInTimeRFC(callbackObj);
    pitRFC.SetParams(accountName, username, "loadPointInTime", data, chartID);
    Globals._rfcMgr.QueueRFC(pitRFC);

    const waitingPanel = new QWaitingPanel();
    waitingPanel.InitWaitingPanel(
      appstr("Loading archive") + "…",
      wsMgr.fBackgroundPanel,
      wsMgr,
      220
    );
    wsMgr.fWaiting = waitingPanel;
  });
};
